import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getDietTemplates, saveDietTemplate, deleteDietTemplate } from '../api/dietitianApi';
import type { DietTemplate } from '../types/dietitian.types';
import { Button } from '../../cabinet/components/ui/Button';
import { toast } from '../../cabinet/components/ui/Toast';

export const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<DietTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [editing, setEditing] = useState<DietTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getDietTemplates();
      setTemplates(res.templates);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editing || !editing.name || !editing.content) return;
    setSaving(true);
    try {
      await saveDietTemplate(editing);
      setEditing(null);
      load();
    } catch (e) {
      toast('Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Usunąć ten szablon?')) return;
    try {
      await deleteDietTemplate(id);
      load();
    } catch (e) {
      toast('Błąd usuwania');
    }
  };

  if (loading) return <div style={styles.loading}>Ładowanie...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1>Twoje szablony zaleceń</h1>
          <div className="muted">Wstawiasz je jednym kliknięciem i dopasowujesz do pacjentki.</div>
        </div>
        <button className="btn" onClick={() => setEditing({ id: '', name: '', content: '' })}>+ Nowy szablon</button>
      </div>
      
      <div style={{ height: 16 }}></div>

      {editing && (
        <div style={styles.editCard}>
          <input
            style={styles.input}
            value={editing.name}
            onChange={e => setEditing({ ...editing, name: e.target.value })}
            placeholder="Nazwa szablonu (np. Insulinooporność)"
          />
          <div style={{ marginBottom: 50, background: '#fff' }}>
            <ReactQuill
              theme="snow"
              value={editing.content}
              onChange={content => setEditing({ ...editing, content })}
              placeholder="Treść zaleceń..."
              style={{ height: 250 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn ghost" onClick={() => setEditing(null)}>Anuluj</button>
            <button className="btn" disabled={saving} onClick={handleSave}>
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </div>
      )}

      <div>
        {templates.map(tpl => (
          <div key={tpl.id} className="tplrow">
            <span style={{ fontSize: 18 }}>📝</span>
            <span className="nm">{tpl.name}</span>
            <span style={{ fontSize: 12.5, color: 'var(--v600)' }}>
              {tpl.content.replace(/<[^>]+>/g, '').length > 50 ? tpl.content.replace(/<[^>]+>/g, '').substring(0, 50) + '...' : tpl.content.replace(/<[^>]+>/g, '')}
            </span>
            <div className="spacer"></div>
            <div className="tpl-actions">
              <a className="link" onClick={() => setEditing(tpl)}>Edytuj</a>
              <a className="link" onClick={() => handleDelete(tpl.id)} style={{ color: 'red' }}>Usuń</a>
            </div>
          </div>
        ))}
        {templates.length === 0 && !editing && (
          <div style={{ textAlign: 'center', color: '#786798', padding: 40 }}>Brak szablonów.</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  wrap: { maxWidth: 680, margin: '0 auto', padding: '20px 0' } as React.CSSProperties,
  loading: { padding: 40, textAlign: 'center' as const, color: '#786798' },
  list: { display: 'flex', flexDirection: 'column', gap: 16 } as React.CSSProperties,
  card: { background: '#fff', border: '1px solid #E6D6FA', borderRadius: 16, padding: 20 },
  editCard: { background: '#FAF8FE', border: '2px solid #BC87FF', borderRadius: 16, padding: 20, marginBottom: 24 },
  preview: { fontSize: 14, color: '#555', whiteSpace: 'pre-wrap' as const, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' },
  input: { width: '100%', height: 44, border: '1.5px solid #E6D6FA', borderRadius: 8, padding: '0 16px', fontSize: 15, marginBottom: 12, fontFamily: 'inherit', outline: 'none' },
  textarea: { width: '100%', height: 200, border: '1.5px solid #E6D6FA', borderRadius: 8, padding: 16, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' as const, outline: 'none' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#361777' },
  iconBtnRed: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'red' },
};
